import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import i18n from "../../i18n";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import { Button } from "../button";
import { useGetFRNTPServer } from "../../hooks/common/query";
import {
  useSyncFRDeviceTimeAndNtpServer,
  useUpdateFRNTPServer,
} from "../../hooks/common/mutation";
import toast from "react-hot-toast";
import Loader from "../loader";

export const DeviceTimeFRConfigurationsCardSchema = Yup.object({
  deviceNTPServer: Yup.string().required(
    i18n.language === "en"
      ? "Device NTP Server is required."
      : "يجب أن يكون جهاز NTP Server مطلوبًا."
  ),
});

interface FormData {
  deviceNTPServer: string;
}

interface DeviceTimeFRConfigurationsCardProps {
  selectedDeviceIPAddresses: string[];
}

const DeviceTimeFRConfigurationsCard = ({
  selectedDeviceIPAddresses,
}: DeviceTimeFRConfigurationsCardProps) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useFormWithSchema(DeviceTimeFRConfigurationsCardSchema);

  const { data: frNTPServer, isPending: isFRNTPServerLoading } =
    useGetFRNTPServer();

  const {
    mutateAsync: UpdateFRNTPServer,
    isPending: isUpdateFRNTPServerLoading,
  } = useUpdateFRNTPServer();

  const {
    mutateAsync: SyncFRDeviceTimeAndNtpServer,
    isPending: isSyncFRDeviceTimeAndNtpServerLoading,
  } = useSyncFRDeviceTimeAndNtpServer();

  useEffect(() => {
    if (frNTPServer) {
      reset({
        deviceNTPServer: frNTPServer.ntpServer,
      });
    }
  }, [frNTPServer, reset]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const body = {
          ntpServer: data.deviceNTPServer,
        };

        await UpdateFRNTPServer(body);

        toast.success(t("deviceTimeUpdatedSuccessfully"));
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }

      if (selectedDeviceIPAddresses.length === 0) {
        toast.error(t("kindlySelectDeviceToSync"));
      } else {
        try {
          const body = {
            ntpServer: data.deviceNTPServer,
            fripAddresses: selectedDeviceIPAddresses,
          };

          await SyncFRDeviceTimeAndNtpServer(body);
          toast.success(t("deviceTimeSyncedSuccessfully"));
        } catch (error) {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      SyncFRDeviceTimeAndNtpServer,
      UpdateFRNTPServer,
      selectedDeviceIPAddresses,
      t,
    ]
  );

  return (
    <div className="flex flex-col gap-2 lg:gap-3">
      <h2 className="text-lg lg:text-xl font-medium">
        {t("deviceTimeConfigurationNTPServer")}
      </h2>

      {isFRNTPServerLoading ? (
        <Loader />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid sm:grid-cols-2 gap-2 md:gap-4 items-end"
        >
          <Controller
            name="deviceNTPServer"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("deviceNTPServer")}
                onChange={onChange}
                value={value || ""}
                error={errors?.[name]?.message}
                required
              />
            )}
          />

          <Button
            isLoading={
              isUpdateFRNTPServerLoading ||
              isSyncFRDeviceTimeAndNtpServerLoading
            }
            className="max-w-max !py-[14px] !px-9"
          >
            {t("pushSync")}
          </Button>
        </form>
      )}
    </div>
  );
};

export default React.memo(DeviceTimeFRConfigurationsCard);
